// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - Admin list page for Session QNA setup

import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"
import {
  useGetSessionQNAsQuery,
} from "./sessionQNAsApiSlice"

const EtSessionQNAList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  // First Created - 25 May 2026
  // Author - Atul Pandey
  // Purpose - To list all Session QNA sets created through Admin setup

  useTitle("Session QNA List")

  const [showDetails, setShowDetails] = useState({})

  const {
    data: sessionQNAsData,
    isError,
    error,
    isLoading,
  } = useGetSessionQNAsQuery()

  const handleToggleDetails = (sessionQNAId) => {
    setShowDetails(prev => ({
      ...prev,
      [sessionQNAId]: !prev[sessionQNAId],
    }))
  }

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>
    )
  }

  const sessionQNAs =
    sessionQNAsData?.ids?.map(id => {
      const sessionQNA = sessionQNAsData?.entities[id]

      return {
        _id: sessionQNA?._id,
        sessionQNAName: sessionQNA?.sessionQNAName,
        sessionQNADesc: sessionQNA?.sessionQNADesc,
        sessionQNAIntro: sessionQNA?.sessionQNAIntro,
        sessionQNAOutro: sessionQNA?.sessionQNAOutro,
        sessionQNAQuestions: sessionQNA?.sessionQNAQuestions || [],
        createdBy: sessionQNA?.createdBy,
        updatedBy: sessionQNA?.updatedBy,
        active: sessionQNA?.active,
      }
    }) || []

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">

      {/* 1 - Header */}
      <div className="flex justify-between items-center m-1 p-1">
        <div className="flex justify-start items-center">
          <p className="text-2xl m-1 p-1 font-bold">Session QNAs</p>

          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />
        </div>
      </div>

      {/* 2 - Empty State */}
      {sessionQNAs?.length === 0 && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No Session QNA sets found. Click the add icon to create a reusable QNA set for sessions.
          </p>
        </div>
      )}

      {/* 3 - List */}
      {sessionQNAs?.map((item, index) => {
        const questions = item?.sessionQNAQuestions || []
        const activeQuestions = questions?.filter(question => question?.active !== false)

        return (
          <div key={item._id} className="m-1 p-1 bg-white rounded">
            <div className="m-1 p-1 bg-gray-100 rounded">

              {/* 3a - Summary Row */}
              <div className="flex justify-between items-center">
                <div className="flex justify-start items-center mx-2">

                  {/* Toggle */}
                  <div
                    onClick={() => handleToggleDetails(item._id)}
                    className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                  >
                    {showDetails[item._id] ? (
                      <MinusIcon className="h-6 w-6 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-orange-600" />
                    )}
                  </div>

                  {/* Title */}
                  <div className="m-1 p-1">
                    <div className="flex justify-start items-center flex-wrap">
                      <p className="font-bold">
                        {index + 1} - {item.sessionQNAName}
                      </p>

                      <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                        {activeQuestions?.length || 0} Active Question{activeQuestions?.length === 1 ? "" : "s"}
                      </p>

                      {!item.active && (
                        <p className="text-[10px] bg-red-100 text-red-700 rounded-full mx-2 px-2 py-1">
                          Inactive
                        </p>
                      )}
                    </div>

                    <p className="text-xs">{item.sessionQNADesc}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-start items-center mx-2">
                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                    <PencilSquareIcon
                      onClick={() => onUpdate(item?._id)}
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />
                  </div>

                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                    <TrashIcon
                      onClick={() => onDelete(item?._id)}
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* 3b - Details */}
              {showDetails[item._id] && (
                <div className="m-1 p-1 bg-gray-200 rounded">
                  <div className="m-1 p-1 rounded bg-white">

                    {/* Intro */}
                    {item?.sessionQNAIntro && (
                      <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                        <p
                          className="m-1 p-1"
                          dangerouslySetInnerHTML={createMarkup(item.sessionQNAIntro)}
                        ></p>
                      </div>
                    )}

                    {/* QNA Summary */}
                    <div className="flex justify-start items-center my-2">
                      <QuestionMarkCircleIcon className="h-4 w-4 text-orange-600 mx-2" />
                      <p className="text-xs font-bold">
                        QNA Set:{" "}
                        <span className="font-normal text-gray-500">
                          {item?.sessionQNAName || "Not defined"}
                        </span>
                      </p>
                    </div>

                    <div className="flex justify-start items-center my-2">
                      <ClipboardDocumentListIcon className="h-4 w-4 text-orange-600 mx-2" />
                      <p className="text-xs font-bold">
                        Total Questions:{" "}
                        <span className="font-normal text-gray-500">
                          {questions?.length || 0}
                        </span>
                      </p>
                    </div>

                    <div className="flex justify-start items-center my-2">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-orange-600 mx-2" />
                      <p className="text-xs font-bold">
                        Active Questions:{" "}
                        <span className="font-normal text-gray-500">
                          {activeQuestions?.length || 0}
                        </span>
                      </p>
                    </div>

                    {/* Questions */}
                    {questions?.length > 0 && (
                      <div className="m-1 p-1">
                        <p className="text-xs font-bold m-1 p-1">
                          Questions
                        </p>

                        {questions
                          ?.slice()
                          ?.sort((a, b) => (a?.questionSequence || 0) - (b?.questionSequence || 0))
                          ?.map((question, questionIndex) => (
                            <div
                              key={question?._id || questionIndex}
                              className="rounded m-1 p-1 bg-gray-200"
                            >
                              <div className="flex justify-between items-start">
                                <div className="m-1 p-1">
                                  <p className="text-[10px] font-bold mx-1 px-1">
                                    Q{question?.questionSequence || questionIndex + 1}: {question?.questionText}
                                  </p>

                                  {question?.questionDesc && (
                                    <p className="text-[10px] mx-1 px-1 text-gray-600">
                                      {question.questionDesc}
                                    </p>
                                  )}

                                  {question?.questionPrompt && (
                                    <p className="text-[10px] mx-1 px-1 text-orange-700 italic">
                                      {question.questionPrompt}
                                    </p>
                                  )}
                                </div>

                                <div className="m-1 p-1 flex justify-end items-center">
                                  {question?.questionType && (
                                    <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-1 px-2 py-1">
                                      {question.questionType}
                                    </p>
                                  )}

                                  {question?.active === false && (
                                    <p className="text-[9px] bg-red-100 text-red-700 rounded-full mx-1 px-2 py-1">
                                      Inactive
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Outro */}
                    {item?.sessionQNAOutro && (
                      <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                        <p
                          className="m-1 p-1"
                          dangerouslySetInnerHTML={createMarkup(item.sessionQNAOutro)}
                        ></p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default EtSessionQNAList